import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Brain, Check, RefreshCcw, Gavel, AlertTriangle, MapPin } from "lucide-react";

export default function JuryPortal() {
    const [queue, setQueue] = useState([]);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState(null);
    const [score, setScore] = useState("");
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState("");

    const load = () => api.get("/jury/queue").then((r) => { setQueue(r.data.queue || []); setLoading(false); });
    useEffect(() => { load(); }, []);

    const openItem = (item) => {
        setActive(item);
        setScore(item.jury_essay_score ?? item.ai_essay_score ?? "");
        setNotes(item.jury_notes || "");
        setMsg("");
    };

    const save = async (finalize) => {
        if (!active) return;
        setBusy(true); setMsg("");
        try {
            await api.post("/jury/override", {
                submission_id: active.id,
                jury_score: Number(score),
                jury_notes: notes,
                finalize,
            });
            setMsg(finalize ? "Đã chốt điểm chính thức." : "Đã lưu nhận xét.");
            await load();
        } catch (e) {
            setMsg(formatApiError(e.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const regrade = async () => {
        if (!active) return;
        setBusy(true);
        try {
            await api.post(`/jury/regrade-ai/${active.id}`);
            setMsg("Đã gửi lại AI chấm. Làm mới sau ít giây.");
            setTimeout(load, 3000);
        } finally {
            setBusy(false);
        }
    };

    return (
        <main className="pt-24 pb-12 min-h-screen">
            <div className="container-x">
                <div className="mb-8 fade-up flex items-center justify-between flex-wrap gap-4">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-[var(--gold-deep)] mb-2 flex items-center gap-2"><Gavel className="w-3 h-3" /> Ban Giám khảo</div>
                        <h1 className="font-heading text-3xl sm:text-4xl font-bold">Hàng đợi chấm tự luận</h1>
                    </div>
                    <button onClick={load} className="btn-secondary !py-2 !px-4"><RefreshCcw className="w-4 h-4" /> Làm mới</button>
                </div>

                <div className="grid lg:grid-cols-12 gap-6">
                    {/* Queue list */}
                    <aside className="lg:col-span-4">
                        <div className="card divide-y divide-[var(--border)] max-h-[75vh] overflow-y-auto">
                            {loading ? (
                                <div className="p-8 text-center text-slate-500">Đang tải…</div>
                            ) : queue.length === 0 ? (
                                <div className="p-8 text-center text-slate-500">Hàng đợi trống.</div>
                            ) : queue.map((q) => (
                                <button key={q.id} onClick={() => openItem(q)}
                                    className={`w-full text-left p-4 transition ${active?.id === q.id ? "bg-[var(--gold)]/10 border-l-4 border-[var(--gold)]" : "hover:bg-[var(--bg-alt)]"}`}
                                    data-testid={`queue-item-${q.id}`}>
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-xs font-mono text-slate-500">{q.user_candidate_code}</span>
                                        <span className={`badge ${q.status === "finalized" ? "badge-cyan" : q.status === "jury_reviewed" ? "badge-gold" : "badge-slate"}`}>{statusLabel(q.status)}</span>
                                    </div>
                                    <div className="font-semibold text-sm truncate">{q.user_name}</div>
                                    <div className="text-xs text-slate-500 mt-1 truncate">{q.contest_title}</div>
                                    <div className="flex items-center gap-3 mt-2 text-xs">
                                        {q.ai_essay_score != null && <span className="text-[var(--gold-deep)] inline-flex items-center gap-1"><Brain className="w-3 h-3" /> {Math.round(q.ai_essay_score)}</span>}
                                        {q.ai_essay_flags?.length > 0 && <span className="text-red-400 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {q.ai_essay_flags.length}</span>}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </aside>

                    {/* Detail */}
                    <section className="lg:col-span-8">
                        {!active ? (
                            <div className="card p-12 text-center text-slate-500">
                                Chọn một bài thi từ hàng đợi để xem chi tiết và chấm.
                            </div>
                        ) : (
                            <div className="space-y-5">
                                <div className="card p-6">
                                    <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                                        <div>
                                            <h2 className="font-heading text-xl font-semibold">{active.user_name}</h2>
                                            <div className="text-xs text-slate-500 flex items-center gap-3 mt-1">
                                                <span className="font-mono">{active.user_candidate_code}</span>
                                                <span><MapPin className="w-3 h-3 inline" /> {active.user_province || "—"}</span>
                                                <span>· {active.contest_title}</span>
                                            </div>
                                        </div>
                                        <span className={`badge ${active.status === "finalized" ? "badge-cyan" : "badge-slate"}`}>{statusLabel(active.status)}</span>
                                    </div>
                                    <div className="grid grid-cols-3 gap-3 text-xs border-t border-[var(--border)] pt-3">
                                        <Stat label="Trắc nghiệm" value={`${Math.round(active.mcq_score_pct || 0)}/100`} />
                                        <Stat label="AI tự luận" value={active.ai_essay_score != null ? `${Math.round(active.ai_essay_score)}/100` : "—"} />
                                        <Stat label="Votes" value={active.votes_count || 0} />
                                    </div>
                                </div>

                                <div className="card p-6">
                                    <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Đề bài</div>
                                    <p className="italic text-slate-600 mb-4">"{active.essay_prompt}"</p>
                                    <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Bài làm</div>
                                    <div className="text-slate-700 leading-relaxed whitespace-pre-line max-h-96 overflow-y-auto p-4 bg-[var(--bg-alt)] rounded-xl border border-[var(--border)]">{active.essay_text}</div>
                                </div>

                                {active.ai_essay_feedback && (
                                    <div className="card p-6 border border-[var(--gold)]/30">
                                        <div className="flex items-center gap-2 mb-3">
                                            <Brain className="w-4 h-4 text-[var(--gold-deep)]" />
                                            <span className="text-xs uppercase tracking-widest text-[var(--gold-deep)]">AI gợi ý ({active.ai_provider_used})</span>
                                            <span className="badge badge-gold ml-auto">Độ tin cậy: {Math.round((active.ai_essay_confidence ?? 0) * 100)}%</span>
                                        </div>
                                        <p className="text-sm text-slate-700">{active.ai_essay_feedback}</p>
                                        {active.ai_essay_flags?.length > 0 && (
                                            <div className="mt-3 flex flex-wrap gap-2">
                                                {active.ai_essay_flags.map((f, i) => <span key={i} className="badge badge-red"><AlertTriangle className="w-3 h-3" /> {f}</span>)}
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="card p-6">
                                    <h3 className="font-heading font-semibold mb-4">Quyết định của Ban Giám khảo</h3>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs uppercase tracking-widest text-slate-500 mb-2">Điểm BGK (0-100)</label>
                                            <input type="number" min={0} max={100} step={0.5} value={score} onChange={(e) => setScore(e.target.value)} className="input" data-testid="jury-score-input" />
                                        </div>
                                        <div>
                                            <label className="block text-xs uppercase tracking-widest text-slate-500 mb-2">Ghi chú</label>
                                            <input value={notes} onChange={(e) => setNotes(e.target.value)} className="input" placeholder="Nhận xét nội bộ…" data-testid="jury-notes-input" />
                                        </div>
                                    </div>
                                    {msg && <div className="text-sm text-[var(--gold-deep)] mt-3">{msg}</div>}
                                    <div className="flex flex-wrap gap-3 mt-4">
                                        <button onClick={() => save(false)} disabled={busy} className="btn-secondary" data-testid="save-review-btn">
                                            <Check className="w-4 h-4" /> Lưu nhận xét
                                        </button>
                                        <button onClick={() => save(true)} disabled={busy} className="btn-primary" data-testid="finalize-btn">
                                            <Gavel className="w-4 h-4" /> Chốt điểm chính thức
                                        </button>
                                        <button onClick={regrade} disabled={busy} className="btn-secondary ml-auto" data-testid="regrade-btn">
                                            <RefreshCcw className="w-4 h-4" /> Yêu cầu AI chấm lại
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </section>
                </div>
            </div>
        </main>
    );
}

function Stat({ label, value }) {
    return (
        <div>
            <div className="text-slate-500 uppercase tracking-widest">{label}</div>
            <div className="text-[var(--navy)] font-semibold mt-1">{value}</div>
        </div>
    );
}
function statusLabel(s) {
    return { submitted: "Chờ xử lý", ai_graded: "AI đã chấm", jury_reviewed: "BGK đã xem", finalized: "Đã chốt" }[s] || s;
}
